import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, ArrowLeft, ShieldCheck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { useAuth, dashboardFor } from '../lib/auth';
import { toast } from 'sonner';
import { useLang } from '../context/LanguageContext';

function strength(pw) {
  let s = 0;
  if (pw.length >= 12) s += 1;
  if (/[A-Z]/.test(pw)) s += 1;
  if (/[a-z]/.test(pw)) s += 1;
  if (/\d/.test(pw)) s += 1;
  if (/[^A-Za-z0-9]/.test(pw)) s += 1;
  return s; // 0–5
}

export default function ResetPasswordPage() {
  const { t } = useLang();
  const STRENGTH_LABEL = [t('rp.sig0'), t('rp.sig1'), t('rp.sig2'), t('rp.sig3'), t('rp.sig4'), t('rp.sig5')];
  const STRENGTH_TINT = ['bg-rose-500', 'bg-rose-400', 'bg-amber-400', 'bg-amber-300', 'bg-emerald-400', 'bg-emerald-600'];
  const nav = useNavigate();
  const auth = useAuth();
  const [email] = useState(() => sessionStorage.getItem('wyse_reset_email') || '');
  const [session] = useState(() => sessionStorage.getItem('wyse_reset_session') || '');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [revokeAll, setRevokeAll] = useState(true);
  const focusRef = useRef();

  useEffect(() => {
    if (!email || !session) {
      nav('/forgot-password', { replace: true });
      return;
    }
    focusRef.current?.focus();
  }, [email, session, nav]);

  const s = useMemo(() => strength(pw), [pw]);
  const meetsPolicy = (
    pw.length >= 12 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) &&
    /\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!meetsPolicy) { toast.error(t('rp.errPolicy')); return; }
    if (pw !== pw2) { toast.error(t('rp.errMatch')); return; }
    setSubmitting(true);
    try {
      const out = await api.passwordReset({
        email, reset_session: session, new_password: pw,
        revoke_other_sessions: revokeAll,
      });
      // Clean session state.
      sessionStorage.removeItem('wyse_reset_email');
      sessionStorage.removeItem('wyse_reset_session');
      // Store the new JWT so the user lands signed in.
      localStorage.setItem('wyse_token', out.access_token);
      localStorage.setItem('wyse_user', JSON.stringify(out.user));
      auth?.refresh?.();
      setDone(true);
      toast.success(t('rp.successToast'));
      setTimeout(() => nav(dashboardFor(out.user.role)), 2200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('rp.errReset'));
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <>
        <Header />
        <section className="min-h-[70vh] flex items-center justify-center px-6 py-14">
          <div data-testid="reset-success"
               className="w-full max-w-md text-center bg-white rounded-2xl border border-neutral-100 shadow p-10">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h1 className="mt-4 text-[24px] font-extrabold text-[#14110f]">{t('rp.successTitle')}</h1>
            <p className="mt-2 text-[13.5px] text-neutral-500">
              {t('rp.successBody')}
            </p>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <section className="min-h-[70vh] flex items-center justify-center px-6 py-14">
        <div data-testid="reset-password-card"
             className="w-full max-w-md bg-white rounded-2xl border border-neutral-100 shadow-[0_25px_60px_-30px_rgba(20,17,15,0.25)] p-8 md:p-10">
          <Link to="/forgot-password" className="inline-flex items-center gap-1.5 text-[12.5px] text-neutral-500 hover:text-[#14110f] mb-6">
            <ArrowLeft className="w-3.5 h-3.5" />{t('rp.back')}
          </Link>
          <p className="section-eyebrow mb-2">{t('rp.eyebrow')}</p>
          <h1 className="text-[26px] font-extrabold text-[#14110f] leading-tight flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#c89200]" />{t('rp.title')}
          </h1>
          <p className="mt-2 text-[13.5px] text-neutral-500">
            {t('rp.desc')}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input data-testid="new-password" ref={focusRef} required
                     type={showPw ? 'text' : 'password'} value={pw}
                     onChange={(e) => setPw(e.target.value)}
                     placeholder={t('rp.newPw')}
                     className="w-full pl-10 pr-11 py-3.5 rounded-md border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px] text-[#14110f]" />
              <button type="button" onClick={() => setShowPw((v) => !v)}
                      data-testid="reset-pw-visibility"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-[#14110f]">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <div data-testid="strength-meter">
              <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                <div className={`h-full transition-all ${STRENGTH_TINT[s]}`} style={{ width: `${(s / 5) * 100}%` }} />
              </div>
              <p className="mt-1.5 text-[11.5px] text-neutral-500">{t('rp.strength')}: <strong className="text-[#14110f]">{STRENGTH_LABEL[s]}</strong></p>
            </div>

            <ul className="text-[11.5px] text-neutral-600 grid grid-cols-2 gap-x-3 gap-y-1">
              {[
                [t('rp.req1'), pw.length >= 12],
                [t('rp.req2'), /[A-Z]/.test(pw)],
                [t('rp.req3'), /[a-z]/.test(pw)],
                [t('rp.req4'), /\d/.test(pw)],
                [t('rp.req5'), /[^A-Za-z0-9]/.test(pw)],
                [t('rp.req6'), pw && pw === pw2],
              ].map(([label, ok]) => (
                <li key={label} className={`flex items-center gap-1.5 ${ok ? 'text-emerald-700' : 'text-neutral-400'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-emerald-500' : 'bg-neutral-300'}`} />
                  {label}
                </li>
              ))}
            </ul>

            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input data-testid="confirm-password" required
                     type={showPw ? 'text' : 'password'} value={pw2}
                     onChange={(e) => setPw2(e.target.value)}
                     placeholder={t('rp.confirmPw')}
                     className="w-full pl-10 pr-4 py-3.5 rounded-md border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px] text-[#14110f]" />
            </div>

            <label className="flex items-center gap-2 text-[12.5px] text-neutral-600">
              <input data-testid="revoke-others" type="checkbox" checked={revokeAll}
                     onChange={(e) => setRevokeAll(e.target.checked)}
                     className="w-3.5 h-3.5 accent-[#f5b300]" />
              {t('rp.revoke')}
            </label>

            <button type="submit" data-testid="reset-submit"
                    disabled={submitting || !meetsPolicy || pw !== pw2}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] disabled:opacity-50 text-[#14110f] font-semibold px-7 py-3.5 rounded-md shadow-[0_14px_30px_-10px_rgba(245,179,0,0.7)] transition-all">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {t('rp.submit')}
            </button>
          </form>
        </div>
      </section>
      <Footer />
    </>
  );
}
